import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { BookmarkRounded } from '@mui/icons-material';
import { validTokenExists } from '../firebaseHelper';
import { addSavedList, deleteSavedList } from '../redux/actions/userActions';
import { hydrateSavedPreviewCache, loadSavedPreviews, savePreview, updatePreviewList } from '../redux/actions/savedPreviewActions';
import { setNextPreviewsInfo } from '../redux/actions/dataActions';
import { loadTrack } from './audioHelper';
import SavedBrowsingHeader from './SavedBrowsingHeader';
import SavedSelectionBar, { SavedSelectionAction } from './SavedSelectionBar';
import SavedHighlightRow from './SavedHighlightRow';
import MenuButton from './MenuButton';
import EmojiPickerModal from './EmojiPickerModal';
import StatusToast from './StatusToast';

const noop = () => {};
const EMPTY_RECORDS = [];

export default function SavedHighlights({ onBack, toggle, listId, onListChange, selectionMode = false, onSelectionModeChange, onSelectionActionChange = noop }) {
    const dispatch = useDispatch();
    const store = useStore();
    const navigate = useNavigate();
    const { uid, session, pages, requests, errors, failedCursors, pending } = useSelector(state => state.savedHighlights);
    const lists = useSelector(state => state.user.savedLists);
    const filter = listId || '__all__';
    const page = pages[filter];
    const records = page?.records || EMPTY_RECORDS;
    const nextCursor = page?.nextCursor;
    const loading = Boolean(requests[filter]);
    const error = errors[filter];
    const [selected, setSelected] = useState(new Set());
    const [dialog, setDialog] = useState(null);
    const [newList, setNewList] = useState(false);
    const [activeAction, setActiveAction] = useState(null);
    const busy = activeAction !== null;
    const [preparing, setPreparing] = useState(false);
    const [message, setMessage] = useState(null);
    const generation = useRef(0);
    const bottom = useRef(null);
    const list = lists.find(value => value.id === listId);

    const isCurrentSession = () => {
        const current = store.getState().savedHighlights;
        return current.uid === uid && current.session === session;
    };

    useEffect(() => {
        generation.current += 1;
        setPreparing(false);
        setSelected(new Set());
        setDialog(null);
        dispatch(hydrateSavedPreviewCache());
        dispatch(loadSavedPreviews(listId)).catch(() => {});
        return () => { generation.current += 1; };
    }, [dispatch, listId, uid, session]);
    useEffect(() => { if (listId && !list) onListChange(null); }, [list, listId, onListChange]);
    useEffect(() => { if (!selectionMode) setSelected(new Set()); }, [selectionMode]);
    useEffect(() => {
        if (!selectionMode || busy) return;
        const availableIds = new Set(records.map(record => record.preview.id));
        setSelected(current => {
            const remaining = new Set([...current].filter(id => availableIds.has(id)));
            return remaining.size === current.size ? current : remaining;
        });
    }, [records, selectionMode, busy]);
    useEffect(() => { setActiveAction(null); setMessage(null); }, [uid, session]);
    useEffect(() => {
        onSelectionActionChange(busy);
        return () => onSelectionActionChange(false);
    }, [busy, onSelectionActionChange]);
    useEffect(() => {
        const retry = () => {
            // Keep a recovery event from superseding the pages needed for playback.
            if (!preparing) dispatch(loadSavedPreviews(listId)).catch(() => {});
        };
        window.addEventListener('online', retry);
        return () => window.removeEventListener('online', retry);
    }, [dispatch, listId, preparing]);
    useEffect(() => {
        if (!bottom.current || !nextCursor || loading || preparing || error) return;
        let active = true;
        const observer = new IntersectionObserver(entries => {
            const current = store.getState().savedHighlights;
            if (!active || !entries.some(entry => entry.isIntersecting)
                || current.uid !== uid || current.session !== session
                || current.requests[filter] || current.errors[filter]) return;
            // Ignore repeated or queued callbacks before React renders the request state.
            active = false;
            observer.disconnect();
            dispatch(loadSavedPreviews(listId, nextCursor)).catch(() => {});
        }, { rootMargin: '200px' });
        observer.observe(bottom.current);
        return () => { active = false; observer.disconnect(); };
    }, [dispatch, store, uid, session, filter, listId, nextCursor, preparing, loading, error]);

    const start = async record => {
        if (preparing || Object.keys(pending).length) return;
        const ownGeneration = ++generation.current;
        setPreparing(true);
        setMessage(null);
        try {
            // Finish this filter before freezing the queue; never append recommendations.
            let ready = store.getState().savedHighlights.pages[filter];
            if (loading || error) ready = await dispatch(loadSavedPreviews(listId));
            const seen = new Set();
            while (ready?.nextCursor && generation.current === ownGeneration) {
                const key = JSON.stringify(ready.nextCursor);
                if (seen.has(key)) throw new Error('Unable to finish loading saved highlights. Please retry.');
                seen.add(key);
                ready = await dispatch(loadSavedPreviews(listId, ready.nextCursor));
            }
            if (generation.current !== ownGeneration || !ready) return;
            if (Object.keys(store.getState().savedHighlights.pending).length) throw new Error('A highlight is still saving. Please try again.');
            const index = ready.records.findIndex(item => item.preview.id === record.preview.id && item.available);
            if (index < 0) throw new Error('This highlight is no longer available in Saved.');
            const queue = ready.records.slice(index).filter(item => item.available && item.preview.mp3URL).map(item => item.preview);
            dispatch(setNextPreviewsInfo(true, queue, { initialPreviewId: record.preview.id, allowRelatedPreviews: false,
                playbackSource: 'saved', playbackOrigin: { kind: 'saved', name: list?.name } }));
            loadTrack(queue[0].mp3URL, 0);
            navigate('/previews', { state: { title: list ? `${list.name} highlights` : 'Saved highlights' } });
        } catch (error) { if (generation.current === ownGeneration) setMessage(error.message); }
        finally { if (generation.current === ownGeneration) setPreparing(false); }
    };
    const execute = async () => {
        if (busy || !dialog || (dialog !== 'list' && selected.size === 0)) return;
        const actionType = dialog;
        setActiveAction(actionType);
        if (actionType !== 'list') setDialog(null);
        let failures = 0;
        try {
            if (actionType === 'list') {
                await validTokenExists();
                if (!isCurrentSession()) return;
                const { data } = await axios.post('/saved_lists_delete', { listId });
                if (!data?.success) throw new Error('Unable to delete list.');
                if (!isCurrentSession()) return;
                dispatch(deleteSavedList(listId));
                onListChange(null);
            } else {
                for (const record of records.filter(item => selected.has(item.preview.id))) {
                    if (!isCurrentSession()) return;
                    try {
                        const action = actionType === 'membership'
                            ? updatePreviewList(record.preview, { name: list.name, listId, remove: true })
                            : savePreview(record.preview, false);
                        await dispatch(action);
                    } catch (_) {
                        failures += 1;
                    }
                }
                if (!isCurrentSession()) return;
                setMessage(failures ? `Couldn't remove ${failures} highlight${failures === 1 ? '' : 's'}. Please try again.`
                    : actionType === 'membership' ? `Removed from ${list.name}` : 'Highlights removed from Saved');
                onSelectionModeChange(false);
                setSelected(new Set());
            }
        } catch (error) { if (isCurrentSession()) setMessage(error.message); }
        finally { if (isCurrentSession()) { setActiveAction(null); setDialog(null); } }
    };
    const createList = async emoji => {
        setNewList(false);
        const current = lists.find(item => item.name === emoji.emoji);
        if (current) { onListChange(current.id); return; }
        try {
            await validTokenExists();
            if (!isCurrentSession()) return;
            const { data } = await axios.post('/saved_lists_new', { name: emoji.emoji });
            if (!isCurrentSession()) return;
            if (!data?.id) throw new Error('Unable to create list.');
            dispatch(addSavedList(emoji.emoji, data.id));
            onListChange(data.id);
        } catch (error) { setMessage(error.message); }
    };
    return <div className='savedTracksRoot'><div className='savedTracksContent'>
        <StatusToast open={Boolean(message)} message={message} onClose={() => setMessage(null)} duration={4000} />
        <SavedBrowsingHeader onBack={onBack} contentType='Highlights' listId={listId} listName={list?.name}
            savedLists={lists} onListChange={onListChange}
            selectionCount={selectionMode ? selected.size : null} controlsDisabled={busy} actions={<div className='savedTracksHeaderActionGroup'>
            {(records.length > 0 || selectionMode) && <button className={selectionMode ? 'savedSelectionCancel' : 'savedTracksHeaderButton'} disabled={busy}
                onClick={() => { onSelectionModeChange(!selectionMode); setSelected(new Set()); }}>{selectionMode ? 'Cancel' : 'Select'}</button>}
            {!selectionMode && <div className='savedTracksHeaderMenuButton'><MenuButton options={listId ? ['Create new list', 'Delete list'] : ['Create new list']}
                handleOptionSelected={option => option === 'Delete list' ? setDialog('list') : setNewList(true)} /></div>}
        </div>}>
            {toggle}
        </SavedBrowsingHeader>
        {preparing && <div className='savedHighlightPreparing' role='status'>Preparing saved highlights… <button onClick={() => { generation.current += 1; setPreparing(false); }}>Cancel</button></div>}
        {records.length === 0 && !nextCursor && !error && <div className='savedHighlightEmpty'>
            {!loading && <div className='savedHighlightEmptyIcon' aria-hidden='true'>{list ? list.name : <BookmarkRounded />}</div>}
            <strong>{loading ? 'Loading saved highlights…' : 'No saved highlights yet'}</strong>
            {!loading && <p>{list ? `Tap a highlight’s bookmark, then choose ${list.name}.` : 'Tap a highlight’s bookmark to save it.'}</p>}
        </div>}
        <div className='previewInfosVerticalList savedHighlightsVerticalList'>{records.map(record => <SavedHighlightRow key={record.preview.id} record={record}
            selectionMode={selectionMode} selectionDisabled={busy} selected={selected.has(record.preview.id)} onPlay={() => start(record)} onSelect={() => !busy && setSelected(before => {
                const next = new Set(before); if (next.has(record.preview.id)) next.delete(record.preview.id); else next.add(record.preview.id); return next;
            })} />)}</div>
        {(nextCursor || loading || error) && <div ref={bottom} className='savedHighlightPagination'>
            {loading && !preparing && <CircularProgress size={24} aria-label='Loading saved highlights' />}
            {error && !loading && <div className='savedHighlightError' role='alert'>
                {error}<button disabled={preparing}
                    onClick={() => dispatch(loadSavedPreviews(listId, failedCursors?.[filter] || null)).catch(() => {})}>Retry</button>
            </div>}
        </div>}
        {selectionMode && <SavedSelectionBar>
            {listId && <SavedSelectionAction disabled={!selected.size || busy} busy={activeAction === 'membership'}
                onClick={() => setDialog('membership')}>Remove from list</SavedSelectionAction>}
            <SavedSelectionAction destructive disabled={!selected.size || busy} busy={activeAction === 'saved'}
                onClick={() => setDialog('saved')} />
        </SavedSelectionBar>}
        <Dialog open={Boolean(dialog)} onClose={() => !busy && setDialog(null)}>
            <DialogTitle>{dialog === 'list' ? 'Delete list?' : dialog === 'membership' ? 'Remove highlights from list?' : 'Remove saved highlights?'}</DialogTitle>
            <DialogContent>{dialog === 'list' ? 'Episodes and highlights will remain saved.' : `${selected.size} highlight${selected.size === 1 ? '' : 's'} will be removed ${dialog === 'membership' ? 'from this list.' : 'from Saved.'}`}</DialogContent>
            <DialogActions><button disabled={busy} onClick={() => setDialog(null)}>Cancel</button>
                <button disabled={busy || (dialog !== 'list' && selected.size === 0)} onClick={execute}>{busy ? 'Removing…' : 'Remove'}</button>
            </DialogActions>
        </Dialog>
        <EmojiPickerModal showEmojiPicker={newList} onClose={() => setNewList(false)} onEmojiClick={createList} />
    </div></div>;
}
